import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  LayoutDashboard, Package, FileText, DollarSign, Users, Settings,
  Truck, Building2, ChevronDown, ChevronRight, LogOut, Menu as MenuIcon,
  Bell, Search, Home, List
} from 'lucide-react';
import { logoutUser } from '../services/api';
import { menuService } from '../services/menuService';
import '../styles/Dashboard.css';

const Dashboard = ({ sessionData, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [menus, setMenus] = useState([]);
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const navigate = useNavigate();
  const location = useLocation();

  // Verify session on mount
  useEffect(() => {
    const verifyDashboardSession = async () => {
      try {
        const response = await fetch('/api/verify-session.php', {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (!data.success) {
          console.error('❌ Session verification failed:', data.message);
          onLogout();
        } else {
          console.log('✅ Session verified on dashboard mount');
        }
      } catch (error) {
        console.error('❌ Session verification error:', error);
      }
    };
    
    verifyDashboardSession();
  }, [onLogout]);

  // Load menus on mount
  useEffect(() => {
    loadMenus();
  }, []);

  const loadMenus = async () => {
    try {
      const userMenus = await menuService.getUserMenus();
      setMenus(userMenus);
      
      // Log menu load status
      if (!userMenus || userMenus.length === 0) {
        console.warn('⚠️ No menus loaded from database');
      } else {
        console.log('✅ Loaded', userMenus.length, 'menu items from database');
      }
      
      // Auto-expand menu containing active route
      const activePath = location.pathname.split('/').pop();
      userMenus.forEach(menu => {
        if (hasActiveChild(menu, activePath)) {
          setExpandedMenus(prev => new Set([...prev, menu.menu_id]));
        }
      });
    } catch (error) {
      console.error('❌ Error loading menus:', error);
      // Set empty menus on error
      setMenus([]);
    }
  };

  // Recursively check if menu or its descendants contain active route
  const hasActiveChild = (menu, activePath) => {
    if (menu.route_path === activePath) {
      return true;
    }
    if (menu.children && menu.children.length > 0) {
      return menu.children.some(child => hasActiveChild(child, activePath));
    }
    return false;
  };

  const getIconComponent = (iconName) => {
    const icons = {
      LayoutDashboard,
      Package,
      FileText,
      DollarSign,
      Users,
      Settings,
      Truck,
      Building2,
      Home,
      List
    };
    return icons[iconName] || Package;
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
      onLogout();
    }
  };

  const isActive = (path) => {
    if (!path) return location.pathname === '/dashboard';
    return location.pathname === `/dashboard/${path}`;
  };

  // RECURSIVE MENU RENDERING - Supports unlimited nesting levels
  const renderMenu = (menu, level = 0) => {
    const hasChildren = menu.children && menu.children.length > 0;
    const isExpanded = expandedMenus.has(menu.menu_id);
    const IconComponent = getIconComponent(menu.icon);

    if (!hasChildren) {
      // Leaf menu item (no children)
      return (
        <li key={menu.menu_id} className="menu-item" style={{ paddingLeft: level > 0 ? `${level * 12}px` : '0' }}>
          <Link 
            to={`/dashboard/${menu.route_path || ''}`}
            className={`menu-link ${isActive(menu.route_path) ? 'active' : ''}`}
          >
            <span className="menu-icon">
              <IconComponent size={20} />
            </span>
            {!collapsed && <span className="menu-text">{menu.menu_name}</span>}
          </Link>
        </li>
      );
    }

    // Parent menu with children
    return (
      <li key={menu.menu_id} className="menu-item" style={{ paddingLeft: level > 0 ? `${level * 12}px` : '0' }}>
        <div 
          className="menu-link"
          onClick={() => toggleMenu(menu.menu_id)}
        >
          <span className="menu-icon">
            <IconComponent size={20} />
          </span>
          {!collapsed && (
            <>
              <span className="menu-text">{menu.menu_name}</span>
              <span className="expand-icon">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            </>
          )}
        </div>
        {!collapsed && isExpanded && (
          <ul className="submenu">
            {menu.children.map(child => renderMenu(child, level + 1))}
          </ul>
        )}
      </li>
    );
  };

  const getCurrentPageTitle = () => {
    const path = location.pathname.split('/').pop();
    if (!path || path === 'dashboard') return 'Dashboard Home';
    
    // Find menu item by route recursively
    const findMenuByRoute = (menus, route) => {
      for (const menu of menus) {
        if (menu.route_path === route) return menu;
        if (menu.children && menu.children.length > 0) {
          const found = findMenuByRoute(menu.children, route);
          if (found) return found;
        }
      }
      return null;
    };
    
    const currentMenu = findMenuByRoute(menus, path);
    return currentMenu ? currentMenu.menu_name : path.replace(/-/g, ' ');
  };

  const getBreadcrumb = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    return pathParts.join(' / ').replace(/-/g, ' ');
  };

  return (
    <div className="dashboard-container">
      {/* SIDEBAR */}
      <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <div className="logo">
            <Truck size={32} />
            {!collapsed && <h2>LTMS</h2>}
          </div>
          <button 
            className="toggle-btn" 
            onClick={() => setCollapsed(!collapsed)}
          >
            <MenuIcon size={20} />
          </button>
        </div>

        {/* User Info */}
        <div className="user-info">
          <div className="user-avatar">
            {(sessionData?.user?.username || sessionData?.session?.username || 'U').charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="user-details">
              <h4>{sessionData?.user?.username || sessionData?.session?.username || 'User'}</h4>
              <p>{sessionData?.company?.companyname || sessionData?.session?.companyname || 'Company'}</p>
              <small>FY: {sessionData?.financial_year?.yearname || sessionData?.session?.yearname || 'N/A'}</small>
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-nav">
          <ul className="menu-list">
            {menus.length === 0 ? (
              <li className="no-menus-message">
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  {!collapsed ? (
                    <>
                      <p style={{ marginBottom: '10px' }}>📋</p>
                      <p style={{ fontSize: '14px' }}>No menus configured</p>
                      <p style={{ fontSize: '12px', marginTop: '5px' }}>
                        Please add menu items to the database
                      </p>
                    </>
                  ) : (
                    <p style={{ fontSize: '20px' }}>📋</p>
                  )}
                </div>
              </li>
            ) : (
              menus.map(menu => renderMenu(menu, 0))
            )}
          </ul>
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-btn">
            <LogOut size={20} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="main-content">
        {/* Top Bar */}
        <div className="top-bar">
          <div className="top-bar-left">
            <h1>{getCurrentPageTitle()}</h1>
            <div className="breadcrumb">{getBreadcrumb()}</div>
          </div>
          <div className="top-bar-right">
            <button className="icon-btn">
              <Search size={20} />
            </button>
            <button className="icon-btn">
              <Bell size={20} />
              <span className="badge">3</span>
            </button>
          </div>
        </div>

        {/* Page Content - This renders child routes */}
        <div className="page-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;